import { useMemo } from "react";
import { Table, Group, Text, ActionIcon, Tooltip } from "@mantine/core";
import { IconEdit } from "@tabler/icons-react";
import { stripHtml } from "../../utils/grossTemplate";
import { GROSS_DESCRIPTION_MATERIALS } from "./specimenConstants";

/** Keep each preview short — strip HTML, collapse whitespace, and cap with an ellipsis. */
const MAX_PREVIEW_CHARS = 80;

function toPreview(html: string | null | undefined): string {
  const text = stripHtml(html || "")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= MAX_PREVIEW_CHARS) return text;
  return `${text.slice(0, MAX_PREVIEW_CHARS).trimEnd()}…`;
}

interface SpecimenPreviewRowProps {
  specimen: any;
  colSpan: number;
  onEdit: () => void;
}

export default function SpecimenPreviewRow({
  specimen,
  colSpan,
  onEdit,
}: SpecimenPreviewRowProps) {
  const commentsText = useMemo(
    () => toPreview(specimen.comments),
    [specimen.comments]
  );
  const grossText = useMemo(() => toPreview(specimen.gross), [specimen.gross]);
  const showGross = useMemo(
    () => GROSS_DESCRIPTION_MATERIALS.has(specimen.material),
    [specimen.material]
  );

  return (
    <Table.Tr>
      <Table.Td
        colSpan={colSpan}
        style={{
          padding: "2px 12px",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="lg" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
            <Text size="xs" c="dimmed" lineClamp={1} style={{ minWidth: 0 }}>
              <Text span size="xs" fw={600} c="dimmed">
                Comments:{" "}
              </Text>
              {commentsText || (
                <Text span size="xs" c="dimmed" fs="italic">
                  Add comments
                </Text>
              )}
            </Text>
            {showGross && (
              <Text size="xs" c="dimmed" lineClamp={1} style={{ minWidth: 0 }}>
                <Text span size="xs" fw={600} c="dimmed">
                  Gross:{" "}
                </Text>
                {grossText || (
                  <Text span size="xs" c="dimmed" fs="italic">
                    Add gross description
                  </Text>
                )}
              </Text>
            )}
          </Group>
          <Tooltip label="Edit specimen details" withinPortal>
            <ActionIcon
              variant="subtle"
              size="sm"
              color="blue"
              onClick={onEdit}
              aria-label="Edit specimen details"
            >
              <IconEdit size={15} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}
